import React, { useState } from "react";
import axios from "axios";
import TopNav from "../../components/navigation/TopNav";
import TimeRangeSelector from "../../components/helpers/TimeRangeSelector";

import { useTranslation } from "react-i18next";
import { maskString, maskFullNames } from "../../components/helpers/maskHelpers";

import StandardScreen, { ScreenContent } from "../../components/layout/StandardScreen";
import Stack from "../../components/layout/Stack";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import FormField from "../../components/forms/FormField";
import TextInput from "../../components/forms/TextInput";
import StatusMessage from "../../components/ui/StatusMessage";
import {
  TableShell,
  Table,
  Th,
  Td,
  NumberTd,
} from "../../components/data/Table";

const narrowShellStyle = {
  width: "100%",
  margin: "0 auto",
};

export default function DriverFromPayersReport() {
  const { t } = useTranslation();

  const [summary, setSummary] = useState([]);
  const [message, setMessage] = useState("");
  const [messageVariant, setMessageVariant] = useState("info");
  const [timeRange, setTimeRange] = useState({ start: "", end: "" });
  const [filterText, setFilterText] = useState("");

  const fetchReport = async () => {
    if (!timeRange.start || !timeRange.end) return;

    axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem("token")}`;

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/driver/payer-transactions-in-time`,
        {
          params: {
            start: timeRange.start,
            end: timeRange.end,
          },
        }
      );

      setSummary(res.data);

      if (res.data.length === 0) {
        setMessage(t("driver_from_payers_report.messages.empty"));
        setMessageVariant("info");
      } else {
        setMessage("");
        setMessageVariant("info");
      }
    } catch (err) {
      console.error("Failed to fetch summary", err);
      setSummary([]);
      setMessage(t("driver_from_payers_report.messages.fetch_failed"));
      setMessageVariant("error");
    }
  };

  const normalizedFilter = filterText.trim().toLowerCase();

  const fromPassengerSummary = summary.filter(
  (item) => item.payer_direction === "from_passenger_to_driver"
);


const filteredSummary = normalizedFilter
  ? fromPassengerSummary.filter((item) => {
      const searchableText = [
        item.from_fullname,
        item.to_fullname,

        item.from_ownerfullname,
        item.to_ownerfullname,

        item.from_sponsor,
        item.to_sponsor,

        item.payer_route,

        item.start_point,
        item.end_point,

        item.from_start_point,
        item.from_end_point,
        item.to_start_point,
        item.to_end_point,

        item.from_owneruserno,
        item.to_owneruserno,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableText.includes(normalizedFilter);
    })
  : fromPassengerSummary;


  return (
    <StandardScreen
      topNav={<TopNav title={t("driver_from_payers_report.title")} mode="main" />}
    >
      <ScreenContent>
        <div style={narrowShellStyle}>
          <Stack gap="4">

            {/* Filters */}
            <Card title={t("driver_from_payers_report.title")}>
              <Stack gap="3">

                <FormField>
                  <div>
                    <TimeRangeSelector onRangeChange={setTimeRange} />
                  </div>
                </FormField>

                <FormField label={t("driver_from_payers_report.fields.search.label")}>
                  <TextInput
                    type="text"
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                    placeholder={t("driver_from_payers_report.fields.search.placeholder")}
                  />
                </FormField>

                <Button onClick={fetchReport} fullWidth>
                  {t("driver_from_payers_report.actions.get_report")}
                </Button>

                <StatusMessage variant={messageVariant} message={message} />
              </Stack>
            </Card>

            {/* Table */}
            {filteredSummary.length > 0 && (
              <Card>
                <TableShell maxHeight="65vh">
                  <Table>
                    <thead>
                      <tr>
                        <Th columnIndex={0} sticky wider> {t("driver_from_payers_report.table.time")} </Th>

                        <Th columnIndex={1} sticky wider> {t("driver_from_payers_report.table.fullname")} </Th>

                        <Th wider> {t("driver_from_payers_report.table.ticket_balance")} </Th>

                        <Th wrap wider>{t("driver_from_payers_report.table.wallet")}</Th>

                        <Th>{t("driver_from_payers_report.table.userno")}</Th>

                        <Th>{t("driver_from_payers_report.table.sponsor")}</Th>

                        <Th>{t("driver_from_payers_report.table.request")}</Th>

                        <Th align="right"> {t("driver_from_payers_report.table.tickets")}</Th>


                      </tr>
                    </thead>

                    <tbody>
                      {filteredSummary.map((item, index) => (
                        <tr key={`${item.treq_id}-${index}`}>
                          <Td columnIndex={0} sticky wider>
                            {new Date(item.created_at)
                              .toLocaleString("en-GB", {
                                day: "2-digit",
                                month: "2-digit",
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: false,
                              })
                              .replace(",", "")}
                          </Td>

                          <Td columnIndex={1} sticky wrap wider> {maskFullNames(item.from_ownerfullname)}</Td>

                          <NumberTd>{item.from_ticket_balance}</NumberTd>

                          <Td wrap wider>{item.from_walletroute}</Td>

                          <Td>{maskString(item.from_owneruserno)}</Td>

                          <Td>{maskFullNames(item.from_sponsor)}</Td>

                          <Td>{t(`driver_from_payers_report.table.${item.request}`)}</Td>

                          <NumberTd>{item.delta_tickets_to_wallet}</NumberTd>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableShell>
              </Card>
            )}

          </Stack>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
}